import { CalcTypes, ConstructionTypes, Layers, Structures } from '@/utils/types'

export function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
}

export function encodeSVG(svg) {
  return svg.replace(/#/g, '%23').replace(/</g, '%3C').replace(/>/g, '%3E')
}

export const transparentFavIcon = encodeSVG(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 18"><path fill="none" d="M-1-1H23V19H-1z"/><path stroke="null" stroke-opacity="null" stroke-width="null" fill="transparent" d="M0-.7H22.1V18.1H0z"/></svg>`)

export function setInputWidth(element) {
  const length = element.getAttribute('placeholder')?.length
  element.setAttribute('size', length)
}

export function setContainerHeight() {
  const selectors = ['.header', '.nav', '.navigation__slider', '.tabs', '.wizard__wrapper', '.wizard__title', '.wizard-footer']
  const margins = ['marginTop', 'marginBottom']
  const paddings = ['paddingTop', 'paddingBottom']

  let height = 0

  selectors.forEach((selector) => {
    const element = document.querySelector(selector)
    const isContainer = selector === '.wizard__wrapper'
    const offsets = isContainer ? [...paddings, ...margins] : [...paddings]

    if (element) {
      offsets.forEach((offset) => {
        height += parseFloat(window.getComputedStyle(element)[offset])
      })
      height += !isContainer ? element.getBoundingClientRect().height : 0
    }
  })

  return `${window.innerHeight - height}px`
}

export const parseInputToNumber = (str) => {
  return str.includes('.') ? parseFloat(parseFloat(str).toFixed(2)) : parseInt(str, 10)
}

export const initialVisibleItems = 3

export const actions = {
  next: 'next',
  back: 'back',
  toStart: 'toStart',
  switchSector: 'switch',
  editSector: 'edit'
}

export function getReportStructure(sector) {
  const type = sector.constructionType.type
  const commonSet = { structure: 'base', screed: 'screed' }
  const hasSizes = sector.system.isSizeable
  const params = {
    [CalcTypes.lite]: {
      [ConstructionTypes.floor]: commonSet,
      [ConstructionTypes.wall]: commonSet
    },
    [CalcTypes.pro]: {
      [ConstructionTypes.floor]: commonSet,
      [ConstructionTypes.wall]: { structure: 'facing', screed: 'isolation' },
      [ConstructionTypes.ceiling]: { structure: 'base', screed: 'isolation' }
    }
  }

  const structureParam = params[sector.algorithm][`${type}`].structure
  const screedParam = params[sector.algorithm][`${type}`].screed
  let calc = hasSizes
    ? getRequestCalcDataRPO(sector, type, true)
    : {
      calcType: sector.algorithm,
      systemId: sector.system?.id,
      structure: {
        id: sector.base.base?.id ?? sector.facing.facing?.id ?? null,
        density: sector.base.density ?? sector.facing.density ?? 0,
        thickness: sector[structureParam].thickness || 0,
        qtySheet: sector.facing.sheets ?? null
      },
      screed: {
        id: sector.screed.screed?.id ?? sector.isolation.isolation?.id ?? null,
        thickness: sector[screedParam]?.thickness?.value || sector[screedParam]?.thickness || 0,
        count: sector.isolation?.thickness?.count ?? null
      },
      location: {
        locationId: sector.constructionType.location.id,
        rating: sector.constructionType.rating,
        isCombinedLocation: sector.constructionType?.isCombinedLocation
      }
    }

  if (sector.system?.isPlaster) {
    calc = {
      calcType: sector.algorithm,
      systemId: sector.system?.id,
      plaster: {
        id: sector.plaster.plaster?.id || null,
        thickness: sector.plaster.thickness || null,
        density: sector.plaster.density || null
      },
      sound: {
        id: sector.isolation?.isolation?.id || null,
        thickness: sector.isolation?.thickness || null
      },
      structure: {
        id: sector.base.base?.id || null,
        density: sector.base.density || null,
        thickness: sector.base.thickness || null
      },
      location: {
        locationId: sector.constructionType.location.id,
        rating: sector.constructionType.rating
      }
    }
  }

  const requestBody = {
    title: sector.name,
    optionSystemId: sector.construction?.id || null,
    calc
  }

  if (sector.soundIsolation?.soundIsolation?.id) {
    requestBody.calc.sound = {
      id: sector.soundIsolation?.soundIsolation?.id,
      thickness: sector.soundIsolation?.thickness
    }
  }

  return requestBody
}

export function getRequestCalcDataRPO(sector, type, isWithSizes) {
  const commonData = {
    systemId: sector.system?.id,
    location: {
      locationId: sector.constructionType.location?.id,
      rating: sector.constructionType?.rating
    }
  }

  const commonDataForWallAndCeiling = {
    structure: {
      id: sector.facing?.facing?.id || null,
      density: sector.facing?.density || null,
      thickness: sector.facing?.thickness || null,
      qtySheet: sector.facing?.sheets || null
    },
    screed: {
      id: sector.isolation?.isolation?.id || null,
      thickness: sector.isolation?.thickness
        ? parseFloat(sector.isolation?.thickness?.value) || sector.isolation?.thickness
        : null,
      count: sector.isolation?.thickness?.count ?? 1
    }
  }

  const commonDataForWallWithPlaster = {
    structure: {
      id: sector.base.base?.id || null,
      density: sector.base.density || null,
      thickness: sector.base.thickness || null
    },
    sound: {
      id: sector.isolation?.isolation?.id || null,
      thickness: sector.isolation?.thickness || null
    },
    plaster: {
      id: sector.plaster.plaster?.id || null,
      thickness: sector.plaster.thickness || null,
      density: sector.plaster.density || null
    }
  }

  const typedData = {
    [ConstructionTypes.floor]: {
      structure: {
        id: sector.base.base?.id || null,
        density: sector.base.density || null,
        thickness: sector.base.thickness || null
      },
      screed: {
        id: sector.screed.screed?.id || null,
        thickness: sector.screed?.thickness || null
      },
      sound: {
        id: sector.soundIsolation?.soundIsolation?.id || null,
        thickness: sector.soundIsolation?.thickness || null
      },
      calcType: sector.algorithm,
      technicalRoomId: null
    },
    [ConstructionTypes.wall]: commonDataForWallAndCeiling,
    [ConstructionTypes.ceiling]: commonDataForWallAndCeiling
  }

  if (isWithSizes) {
    const { x, y } = sector.sizes

    typedData[type].structure = {}
    typedData[type].structure = {
      id: sector.base?.base?.id || null,
      thickness: sector.base?.thickness || null,
      density: sector.base?.density || null,
      y: y ? parseInputToNumber(y) : null,
      x: x ? parseInputToNumber(x) : null
    }
    typedData[ConstructionTypes.wall].claddings = defineLayers(sector)
    typedData[type].qtySheet = sector.facing?.sheets
    typedData[type].sound = {
      id: sector.isolation?.isolation?.id,
      thickness: sector.isolation?.thickness || null
    }
    typedData[type].calcType = sector.algorithm

    delete typedData[type].screed
  }

  const data = sector.system?.isPlaster ? commonDataForWallWithPlaster : {
    ...typedData[type ?? ConstructionTypes.wall]
  }

  return { ...commonData, ...data }
}

const defineLayers = (sector) => {
  const withLayers = sector.facing?.sheets && sector.facing?.sheets > 1
  const hasSameLayers = sector.facingInnerLayer.facingInnerLayerBlocked
  const layers = getLayers(sector)
  const facingOuterLayer = layers[0]

  if (withLayers) {
    return hasSameLayers
      ? [facingOuterLayer, { ...facingOuterLayer, type: Layers.inner }]
      : layers
  } else {
    return [facingOuterLayer]
  }
}

const getLayers = (sector) => {
  return [
    [Structures.facing, sector.facing],
    [Structures.facingInnerLayer, sector.facingInnerLayer]
  ]
    .map(([key, layer]) => {
      const { density, thickness } = layer
      const id = layer[key]?.id || null
      const type = key === Structures.facing ? Layers.outer : Layers.inner

      return { id, density, thickness, type }
    })
}

export const wasDataForRequestActual = (preparedData, sector, constructionType, isWithSizes, isWithPlaster) => {
  const commonRule = preparedData.structure.density === sector.facing.density && preparedData.structure.thickness === sector.facing.thickness
  const keyForWallPro = isWithPlaster ? Structures.base : isWithSizes ? Structures.base : Structures.facing

  const rules = {
    [ConstructionTypes.floor]: {
      [CalcTypes.pro]: preparedData.structure.density === sector.base.density && preparedData.structure.thickness === sector.base.thickness,
      [CalcTypes.lite]: commonRule
    },
    [ConstructionTypes.wall]: {
      [CalcTypes.pro]: preparedData.structure.density === sector[`${keyForWallPro}`].density &&
      preparedData.structure.thickness === sector[`${keyForWallPro}`].thickness,
      [CalcTypes.lite]: commonRule
    },
    [ConstructionTypes.ceiling]: {
      [CalcTypes.pro]: preparedData.structure.density === sector.base.density &&
      preparedData.structure.thickness === sector.base.thickness
    }
  }

  return rules[constructionType][sector.algorithm]
}
